import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import { TableKit } from "@tiptap/extension-table";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Undo2, Redo2, Link2, Link2Off, Table, Paintbrush, EyeOff, MoreHorizontal, Rows3, Columns3, TableRowsSplit, TableColumnsSplit, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { authFetch } from "@/adminAuth";

export default function RichEditor({ initial, onChange }) {
  const [painterArmed, setPainterArmed] = useState(false);
  const painterFormat = useRef(null);
  const painterFrom = useRef(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkResults, setLinkResults] = useState(null);
  const linkTimer = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({ openOnClick: false, autolink: true }),
      TableKit,
      TextStyleKit,
    ],
    content: initial,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    onSelectionUpdate: ({ editor }) => {
      // Pinceau : des qu'une nouvelle selection apparait, on applique le format capture
      if (!painterFormat.current) return;
      const { from, empty } = editor.state.selection;
      if (empty || from === painterFrom.current) return;
      const f = painterFormat.current;
      const chain = editor.chain().focus();
      if (f.bold) chain.setMark("bold"); else chain.unsetMark("bold");
      if (f.italic) chain.setMark("italic"); else chain.unsetMark("italic");
      if (f.fontSize) chain.setFontSize(f.fontSize); else chain.unsetFontSize();
      if (f.block === "h2") chain.setNode("heading", { level: 2 });
      else if (f.block === "h3") chain.setNode("heading", { level: 3 });
      chain.run();
      painterFormat.current = null;
      setPainterArmed(false);
    },
  });

  // Recherche de fiches (debounce 300 ms) pour le selecteur de liens
  useEffect(() => {
    if (!linkOpen) return;
    clearTimeout(linkTimer.current);
    if (linkQuery.trim().length < 2) {
      setLinkResults(null);
      return;
    }
    linkTimer.current = setTimeout(() => {
      authFetch(`/api/admin/knowledge-search?q=${encodeURIComponent(linkQuery)}`)
        .then((r) => r.json())
        .then(setLinkResults)
        .catch(() => setLinkResults([]));
    }, 300);
    return () => clearTimeout(linkTimer.current);
  }, [linkQuery, linkOpen]);

  if (!editor) return null;

  const handlePainter = () => {
    if (painterArmed) {
      painterFormat.current = null;
      setPainterArmed(false);
      return;
    }
    painterFormat.current = {
      bold: editor.isActive("bold"),
      italic: editor.isActive("italic"),
      fontSize: editor.getAttributes("textStyle").fontSize || null,
      block: editor.isActive("heading", { level: 2 }) ? "h2" : editor.isActive("heading", { level: 3 }) ? "h3" : "p",
    };
    painterFrom.current = editor.state.selection.from;
    setPainterArmed(true);
  };

  const markInternal = () => {
    const { from, to, empty } = editor.state.selection;
    if (empty) {
      editor.chain().focus().insertContent("[[interne]] texte à vérifier [[/interne]]").run();
      return;
    }
    editor.chain().focus().insertContentAt(to, "[[/interne]]").insertContentAt(from, "[[interne]]").run();
  };

  const setSize = (v) => {
    if (v) editor.chain().focus().setFontSize(v).run();
    else editor.chain().focus().unsetFontSize().run();
  };

  const openLinkDialog = () => {
    setLinkUrl(editor.getAttributes("link").href || "");
    setLinkQuery("");
    setLinkResults(null);
    setLinkOpen(true);
  };

  const applyLink = (href, label) => {
    const { empty } = editor.state.selection;
    if (empty && label) {
      editor.chain().focus().insertContent({ type: "text", text: label, marks: [{ type: "link", attrs: { href } }] }).run();
    } else {
      editor.chain().focus().setLink({ href }).run();
    }
    setLinkOpen(false);
  };

  const btn = "p-2 text-[#A0A5B1] hover:text-gold hover:bg-white/5 transition-colors rounded disabled:opacity-30 disabled:hover:text-[#A0A5B1]";
  const menuItem = "cursor-pointer text-sm gap-2 focus:bg-white/10 focus:text-gold-light data-[highlighted]:bg-white/10 data-[highlighted]:text-gold-light";
  const inTable = editor.isActive("table");

  return (
    <div data-testid="rich-editor" className="border border-white/10 bg-ink-soft">
      <div className="sticky top-16 lg:top-20 z-30 flex flex-wrap items-center gap-1 p-2 border-b border-white/10 bg-ink-soft" data-testid="rich-editor-toolbar">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} data-active={editor.isActive("bold")} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Gras" data-testid="editor-gras">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} data-active={editor.isActive("italic")} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Italique" data-testid="editor-italique">
          <Italic className="w-4 h-4" />
        </button>
        <select
          value={editor.getAttributes("textStyle").fontSize || ""}
          onChange={(e) => setSize(e.target.value)}
          data-testid="editor-font-size"
          title="Taille du texte"
          className="h-8 mx-0.5 px-1.5 bg-ink-soft border border-white/10 text-[#A0A5B1] text-xs hover:text-gold focus:border-gold outline-none cursor-pointer"
        >
          <option value="">Taille normale</option>
          <option value="0.85em">Petit</option>
          <option value="1.15em">Grand</option>
          <option value="1.35em">Très grand</option>
        </select>
        <span className="w-px bg-white/10 mx-1 my-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} data-active={editor.isActive("heading", { level: 2 })} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Titre" data-testid="editor-titre">
          <Heading2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} data-active={editor.isActive("heading", { level: 3 })} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Sous-titre" data-testid="editor-sous-titre">
          <Heading3 className="w-4 h-4" />
        </button>
        <span className="w-px bg-white/10 mx-1 my-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} data-active={editor.isActive("bulletList")} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Liste" data-testid="editor-liste">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} data-active={editor.isActive("orderedList")} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Liste numérotée" data-testid="editor-liste-num-rot-e">
          <ListOrdered className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} data-active={editor.isActive("blockquote")} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Citation" data-testid="editor-citation">
          <Quote className="w-4 h-4" />
        </button>
        <span className="w-px bg-white/10 mx-1 my-1" />
        <button type="button" onClick={openLinkDialog} data-active={editor.isActive("link")} className={`${btn} data-[active=true]:text-gold data-[active=true]:bg-gold/10`} title="Lien (rechercher une fiche ou coller une URL)" data-testid="editor-lien">
          <Link2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().unsetLink().run()} disabled={!editor.isActive("link")} className={btn} title="Retirer le lien" data-testid="editor-retirer-le-lien">
          <Link2Off className="w-4 h-4" />
        </button>
        <span className="w-px bg-white/10 mx-1 my-1" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={btn} title="Tableau" data-testid="editor-menu-tableau">
              <Table className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-ink-soft border-white/10 text-[#FAF8F5] min-w-[220px]">
            <DropdownMenuItem className={menuItem} onSelect={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
              <Table className="w-4 h-4 text-gold" /> Insérer un tableau (3×3)
            </DropdownMenuItem>
            <DropdownMenuItem className={menuItem} disabled={!inTable} onSelect={() => editor.chain().focus().addRowAfter().run()}>
              <Rows3 className="w-4 h-4 text-gold" /> Ajouter une ligne
            </DropdownMenuItem>
            <DropdownMenuItem className={menuItem} disabled={!inTable} onSelect={() => editor.chain().focus().addColumnAfter().run()}>
              <Columns3 className="w-4 h-4 text-gold" /> Ajouter une colonne
            </DropdownMenuItem>
            <DropdownMenuItem className={menuItem} disabled={!inTable} onSelect={() => editor.chain().focus().deleteRow().run()}>
              <TableRowsSplit className="w-4 h-4 text-gold" /> Supprimer la ligne
            </DropdownMenuItem>
            <DropdownMenuItem className={menuItem} disabled={!inTable} onSelect={() => editor.chain().focus().deleteColumn().run()}>
              <TableColumnsSplit className="w-4 h-4 text-gold" /> Supprimer la colonne
            </DropdownMenuItem>
            <DropdownMenuItem className={menuItem} disabled={!inTable} onSelect={() => editor.chain().focus().deleteTable().run()}>
              <Trash2 className="w-4 h-4 text-gold" /> Supprimer le tableau
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={btn} title="Plus d'outils" data-testid="editor-menu-outils">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-ink-soft border-white/10 text-[#FAF8F5] min-w-[260px]">
            <DropdownMenuItem className={menuItem} onSelect={handlePainter} data-active={painterArmed}>
              <Paintbrush className={`w-4 h-4 ${painterArmed ? "text-gold" : "text-gold"}`} />
              {painterArmed ? "Annuler le pinceau" : "Pinceau : copier la mise en forme"}
            </DropdownMenuItem>
            <DropdownMenuItem className={menuItem} onSelect={markInternal}>
              <EyeOff className="w-4 h-4 text-gold" /> Texte interne (masqué au public)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <span className="w-px bg-white/10 mx-1 my-1" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} className={btn} title="Annuler" data-testid="editor-annuler">
          <Undo2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} className={btn} title="Rétablir" data-testid="editor-r-tabler">
          <Redo2 className="w-4 h-4" />
        </button>
      </div>
      <p className="px-3 py-1.5 border-b border-white/10 font-mono text-[10px] text-[#626773]">
        Astuce : encadrez un passage douteux avec [[interne]] … [[/interne]] (menu ⋯) — il ne sera visible que dans ce back-office, jamais sur le site public.
      </p>
      {linkOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-28 bg-black/70 px-4" onClick={() => setLinkOpen(false)} data-testid="editor-link-dialog">
          <div className="w-[520px] max-w-full bg-ink border border-gold/30 shadow-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">Insérer un lien</p>
            <input
              autoFocus
              value={linkQuery}
              onChange={(e) => setLinkQuery(e.target.value)}
              placeholder="Rechercher une fiche par mots — ex : cession actifs"
              data-testid="editor-link-search"
              className="w-full bg-ink-soft border border-white/10 px-3 py-2 text-sm text-[#FAF8F5] placeholder:text-[#626773] outline-none focus:border-gold"
            />
            {linkResults !== null && (
              <div className="max-h-56 overflow-y-auto border border-white/10 divide-y divide-white/5">
                {linkResults.length === 0 && (
                  <p className="px-3 py-3 text-xs text-[#626773]">Aucune fiche trouvée.</p>
                )}
                {linkResults.map((r) => (
                  <button
                    key={`${r.type}-${r.slug}`}
                    type="button"
                    onClick={() => applyLink(`/ressources/${r.type}/${r.slug}`, r.title)}
                    data-testid={`editor-link-result-${r.slug}`}
                    className="w-full text-left px-3 py-2.5 hover:bg-gold/10 transition-colors"
                  >
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold/70">{r.type_label || r.type}</span>
                    <span className="block text-sm text-[#FAF8F5] leading-snug">{r.title}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="…ou collez une URL (https:// ou /ressources/…)"
                data-testid="editor-link-url"
                className="flex-1 bg-ink-soft border border-white/10 px-3 py-2 text-sm text-[#FAF8F5] placeholder:text-[#626773] outline-none focus:border-gold"
              />
              <button
                type="button"
                onClick={() => linkUrl.trim() && applyLink(linkUrl.trim(), linkUrl.trim())}
                data-testid="editor-link-apply"
                className="px-4 py-2 bg-gold text-ink text-xs uppercase tracking-[0.15em] hover:bg-gold-light transition-colors"
              >
                Appliquer
              </button>
            </div>
          </div>
        </div>
      )}
      <EditorContent editor={editor} className="fiche-content px-4 py-4" data-testid="rich-editor-content" />
    </div>
  );
}
